import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Gear, Check, Plus, Trash, Briefcase, Clock, PencilSimple, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const formatCLP = (amount) => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency', currency: 'CLP',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(amount);
};

const SettingsPage = () => {
  const { axiosInstance } = useAuth();
  const [settings, setSettings] = useState(null);
  const [cargos, setCargos] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedShiftBranch, setSelectedShiftBranch] = useState('');
  const [newCargo, setNewCargo] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [addingCargo, setAddingCargo] = useState(false);

  // Shift Templates state
  const [shiftTemplates, setShiftTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ code: '', name: '', start_time: '', end_time: '', areas: [], schedule: {} });

  // Cargo Rules state
  const [cargoRules, setCargoRules] = useState([]);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm, setRuleForm] = useState({ cargo: '', shift_codes: [], min_workers: 1, is_variable: false, branch_id: '' });

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const branchParam = selectedShiftBranch ? `?branch_id=${selectedShiftBranch}` : '';
      const [settingsRes, cargosRes, branchesRes, templatesRes, rulesRes] = await Promise.all([
        axiosInstance.get('/api/settings'),
        axiosInstance.get('/api/cargos'),
        axiosInstance.get('/api/branches'),
        axiosInstance.get(`/api/shift-templates${branchParam}`),
        axiosInstance.get(`/api/cargo-shift-rules${branchParam}`),
      ]);
      setSettings(settingsRes.data);
      setCargos(cargosRes.data);
      setBranches(branchesRes.data);
      setShiftTemplates(templatesRes.data);
      setCargoRules(rulesRes.data);
    } catch (error) {
      // error handled silently
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, selectedShiftBranch]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await axiosInstance.put('/api/settings', settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleAddCargo = async () => {
    if (!newCargo.trim()) return;
    setAddingCargo(true);
    try {
      await axiosInstance.post('/api/cargos', { name: newCargo.trim() });
      setNewCargo('');
      toast.success('Cargo agregado');
      loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al agregar cargo');
    } finally {
      setAddingCargo(false);
    }
  };

  const handleToggleCargo = async (cargo) => {
    try {
      await axiosInstance.put(`/api/cargos/${cargo.id}`, { is_active: !cargo.is_active });
      toast.success(cargo.is_active ? 'Cargo desactivado' : 'Cargo activado');
      loadSettings();
    } catch (err) {
      toast.error('Error al actualizar cargo');
    }
  };

  // ── Shift Templates handlers ──
  const openTemplateModal = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateForm({
        code: template.code,
        name: template.name || '',
        start_time: template.start_time,
        end_time: template.end_time || '',
        areas: template.areas || [],
        schedule: template.schedule || {},
        branch_id: template.branch_id || selectedShiftBranch || '',
      });
    } else {
      setEditingTemplate(null);
      setTemplateForm({ code: '', name: '', start_time: '', end_time: '', areas: [], schedule: {}, branch_id: selectedShiftBranch || '' });
    }
    setShowTemplateModal(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.code.trim() || !templateForm.start_time.trim()) {
      toast.error('Codigo y hora inicio son obligatorios');
      return;
    }
    try {
      if (editingTemplate) {
        await axiosInstance.put(`/api/shift-templates/${editingTemplate.id}`, templateForm);
        toast.success('Plantilla actualizada');
      } else {
        await axiosInstance.post('/api/shift-templates', templateForm);
        toast.success('Plantilla creada');
      }
      setShowTemplateModal(false);
      loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar plantilla');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('¿Eliminar esta plantilla de turno?')) return;
    try {
      await axiosInstance.delete(`/api/shift-templates/${id}`);
      toast.success('Plantilla eliminada');
      loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  // ── Cargo Rules handlers ──
  const openRuleModal = (rule = null) => {
    if (rule) {
      setEditingRule(rule);
      setRuleForm({ cargo: rule.cargo, shift_codes: rule.shift_codes || [], min_workers: rule.min_workers || 1, is_variable: rule.is_variable || false, branch_id: rule.branch_id || selectedShiftBranch || '' });
    } else {
      setEditingRule(null);
      setRuleForm({ cargo: '', shift_codes: [], min_workers: 1, is_variable: false, branch_id: selectedShiftBranch || '' });
    }
    setShowRuleModal(true);
  };

  const handleSaveRule = async () => {
    if (!ruleForm.cargo.trim() || ruleForm.shift_codes.length === 0) {
      toast.error('Cargo y al menos un turno permitido son obligatorios');
      return;
    }
    try {
      if (editingRule) {
        await axiosInstance.put(`/api/cargo-shift-rules/${editingRule.id}`, ruleForm);
        toast.success('Regla actualizada');
      } else {
        await axiosInstance.post('/api/cargo-shift-rules', ruleForm);
        toast.success('Regla creada');
      }
      setShowRuleModal(false);
      loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar regla');
    }
  };

  const handleDeleteRule = async (id) => {
    if (!window.confirm('¿Eliminar esta regla de cargo?')) return;
    try {
      await axiosInstance.delete(`/api/cargo-shift-rules/${id}`);
      toast.success('Regla eliminada');
      loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const toggleShiftCode = (code) => {
    setRuleForm(prev => ({
      ...prev,
      shift_codes: prev.shift_codes.includes(code)
        ? prev.shift_codes.filter(c => c !== code)
        : [...prev.shift_codes, code],
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  const activeCargos = cargos.filter(c => c.is_active);
  const cargosWithoutRules = activeCargos.filter(c => !cargoRules.find(r => r.cargo === c.name));

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-900" data-testid="settings-title">
            Configuracion
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Parametros generales del sistema</p>
        </div>
        <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="save-settings-btn">
          {saving ? (<><div className="spinner"></div>Guardando...</>) : saved ? (<><Check size={18} />Guardado</>) : ('Guardar Cambios')}
        </button>
      </div>

      <div className="space-y-6">
        {/* Advance Limits */}
        <div className="border border-zinc-200 bg-white p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-zinc-100"><Gear size={20} className="text-zinc-600" /></div>
            <div>
              <h2 className="font-heading font-semibold text-zinc-900">Limites de Anticipos</h2>
              <p className="text-sm text-zinc-500">Configure los limites y restricciones</p>
            </div>
          </div>
          <div className="space-y-6">
            <div>
              <label className="form-label">Limite mensual por trabajador (CLP)</label>
              <input type="number" value={settings?.monthly_advance_limit || 0}
                onChange={(e) => setSettings({ ...settings, monthly_advance_limit: parseFloat(e.target.value) || 0 })}
                className="form-input w-48 font-mono" min="0" step="10000" data-testid="monthly-limit-input" />
              <p className="text-xs text-zinc-500 mt-1">
                El sistema alertara cuando un trabajador supere este monto mensual.
                {settings?.monthly_advance_limit > 0 && (
                  <span className="block mt-1">Limite actual: <strong>{formatCLP(settings.monthly_advance_limit)}</strong></span>
                )}
              </p>
            </div>
            <div className="flex items-center justify-between py-4 border-t border-zinc-100">
              <div>
                <p className="text-sm font-medium text-zinc-900">Permitir multiples anticipos diarios</p>
                <p className="text-xs text-zinc-500">Si esta desactivado, cada trabajador solo podra recibir un anticipo por dia.</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={settings?.allow_multiple_daily_advances || false}
                  onChange={(e) => setSettings({ ...settings, allow_multiple_daily_advances: e.target.checked })}
                  className="sr-only peer" data-testid="allow-multiple-daily-toggle" />
                <div className="w-11 h-6 bg-zinc-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-zinc-900 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-zinc-300 after:border after:h-5 after:w-5 after:transition-all peer-checked:bg-zinc-900"></div>
              </label>
            </div>
            <div className="flex items-center justify-between py-4 border-t border-zinc-100">
              <div>
                <p className="text-sm font-medium text-zinc-900">Permitir anticipos a trabajadores inactivos</p>
                <p className="text-xs text-zinc-500">Si esta desactivado, no se podran registrar anticipos a trabajadores con estado diferente a "Activo".</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={settings?.allow_inactive_worker_advances || false}
                  onChange={(e) => setSettings({ ...settings, allow_inactive_worker_advances: e.target.checked })}
                  className="sr-only peer" data-testid="allow-inactive-toggle" />
                <div className="w-11 h-6 bg-zinc-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-zinc-900 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-zinc-300 after:border after:h-5 after:w-5 after:transition-all peer-checked:bg-zinc-900"></div>
              </label>
            </div>
            <div className="flex items-center justify-between py-4 border-t border-zinc-100">
              <div>
                <p className="text-sm font-medium text-zinc-900">Vacaciones cuentan como costo laboral</p>
                <p className="text-xs text-zinc-500">Si esta activado, los dias de vacaciones sumaran el valor dia del trabajador al costo laboral.</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={settings?.vacations_count_as_labor_cost || false}
                  onChange={(e) => setSettings({ ...settings, vacations_count_as_labor_cost: e.target.checked })}
                  className="sr-only peer" data-testid="vacations-cost-toggle" />
                <div className="w-11 h-6 bg-zinc-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-zinc-900 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-zinc-300 after:border after:h-5 after:w-5 after:transition-all peer-checked:bg-zinc-900"></div>
              </label>
            </div>
            <div className="flex items-center justify-between py-4 border-t border-zinc-100">
              <div>
                <p className="text-sm font-medium text-zinc-900">Permitir modificar dias anteriores a hoy</p>
                <p className="text-xs text-zinc-500">Si esta desactivado, los encargados de sucursal no podran editar celdas de dias anteriores a la fecha actual en planilla mensual, anticipos ni horas extras. El administrador siempre puede editar.</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={settings?.allow_edit_past_days ?? true}
                  onChange={(e) => setSettings({ ...settings, allow_edit_past_days: e.target.checked })}
                  className="sr-only peer" data-testid="allow-edit-past-days-toggle" />
                <div className="w-11 h-6 bg-zinc-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-zinc-900 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-zinc-300 after:border after:h-5 after:w-5 after:transition-all peer-checked:bg-zinc-900"></div>
              </label>
            </div>
          </div>
        </div>

        {/* Shifts General Config */}
        <div className="border border-zinc-200 bg-white p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-zinc-100"><Clock size={20} className="text-zinc-600" /></div>
            <div>
              <h2 className="font-heading font-semibold text-zinc-900">Configuracion de Turnos</h2>
              <p className="text-xs text-zinc-500">Parametros para el modulo de asignacion de turnos</p>
            </div>
          </div>
          <div className="space-y-0">
            <div className="flex items-center justify-between py-4">
              <div>
                <p className="text-sm font-medium text-zinc-900">Tiempo de colacion por turno (minutos)</p>
                <p className="text-xs text-zinc-500">Se descuenta automaticamente de cada turno para calcular las horas efectivas trabajadas.</p>
              </div>
              <input type="number" value={settings?.colacion_minutes ?? 30}
                onChange={(e) => setSettings({ ...settings, colacion_minutes: parseInt(e.target.value) || 0 })}
                className="form-input w-24 text-center font-mono" min="0" max="120" step="5" data-testid="colacion-minutes-input" />
            </div>
            <div className="py-4 border-t border-zinc-100">
              <div className="mb-3">
                <p className="text-sm font-medium text-zinc-900">Dias preferentes de descanso</p>
                <p className="text-xs text-zinc-500">El generador automatico asignara descansos preferentemente en estos dias.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {['Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado', 'Domingo'].map((day, idx) => {
                  const restDays = settings?.preferred_rest_days || [0, 1, 2];
                  const isSelected = restDays.includes(idx);
                  return (
                    <button key={day} type="button"
                      onClick={() => {
                        const updated = isSelected ? restDays.filter(d => d !== idx) : [...restDays, idx];
                        setSettings({ ...settings, preferred_rest_days: updated });
                      }}
                      className={`px-3 py-1.5 text-xs font-medium border transition-colors ${
                        isSelected ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400'
                      }`} data-testid={`rest-day-${idx}`}>
                      {day}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* ─── SHIFT TEMPLATES ─── */}
        <div className="border border-zinc-200 bg-white p-6" data-testid="shift-templates-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-50">
                <Clock size={20} className="text-emerald-600" />
              </div>
              <div>
                <h2 className="font-heading font-semibold text-zinc-900">Plantillas de Turno</h2>
                <p className="text-xs text-zinc-500">Cada sucursal tiene sus propias plantillas y reglas de turno</p>
              </div>
            </div>
            <button onClick={() => openTemplateModal()} className="btn-primary flex items-center gap-2 text-sm" data-testid="add-template-btn">
              <Plus size={16} /> Nueva Plantilla
            </button>
          </div>
          <div className="mb-4 flex items-center gap-2">
            <label className="text-xs font-medium text-zinc-600">Sucursal:</label>
            <select
              value={selectedShiftBranch}
              onChange={(e) => setSelectedShiftBranch(e.target.value)}
              className="form-input text-sm py-1.5 w-auto min-w-[200px]"
              data-testid="shift-branch-select"
            >
              <option value="">Todas las sucursales</option>
              {branches.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>

          {shiftTemplates.length === 0 ? (
            <div className="text-center py-8 border border-dashed border-zinc-200">
              <Clock size={32} className="text-zinc-300 mx-auto mb-2" />
              <p className="text-sm text-zinc-400">No hay plantillas configuradas</p>
              <p className="text-xs text-zinc-400 mt-1">El generador usara turnos genericos manana/tarde</p>
            </div>
          ) : (
            <div className="grid gap-2">
              {shiftTemplates.map(t => {
                const hasSched = t.schedule && Object.keys(t.schedule).length > 0;
                const dayKeys = ['lun','mar','mie','jue','vie','sab','dom'];
                const dayLabels = ['L','M','X','J','V','S','D'];
                const branchName = t.branch_id ? branches.find(b => b.id === t.branch_id)?.name : null;
                return (
                <div key={t.id} className="px-4 py-3 border border-zinc-100 hover:border-zinc-300 transition-colors" data-testid={`template-item-${t.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="inline-flex items-center justify-center w-9 h-9 bg-emerald-100 text-emerald-700 font-bold text-sm">{t.code}</span>
                      <div>
                        <p className="text-sm font-medium text-zinc-900">{t.name || `Turno ${t.code}`}</p>
                        <p className="text-xs text-zinc-500 font-mono">
                          {hasSched ? 'Horario por dia' : `${t.start_time} - ${t.end_time || '(variable)'}`}
                        </p>
                        {branchName && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-zinc-100 text-zinc-600 border border-zinc-200 mt-0.5 inline-block">{branchName}</span>
                        )}
                        {!branchName && !selectedShiftBranch && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-600 border border-amber-200 mt-0.5 inline-block">Global</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => openTemplateModal(t)} className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors" data-testid={`edit-template-${t.id}`}>
                        <PencilSimple size={16} />
                      </button>
                      <button onClick={() => handleDeleteTemplate(t.id)} className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors" data-testid={`delete-template-${t.id}`}>
                        <Trash size={16} />
                      </button>
                    </div>
                  </div>
                  {(t.areas?.length > 0 || hasSched) && (
                    <div className="mt-2 pl-13 space-y-1.5" style={{ paddingLeft: '52px' }}>
                      {t.areas?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {t.areas.map(a => <span key={a} className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-100">{a}</span>)}
                        </div>
                      )}
                      {hasSched && (
                        <div className="flex gap-1 flex-wrap">
                          {dayKeys.map((dk, di) => {
                            const ds = t.schedule[dk];
                            return (
                              <div key={dk} className={`text-center px-1.5 py-1 text-[10px] border ${ds ? 'border-emerald-200 bg-emerald-50' : 'border-zinc-100 bg-zinc-50 text-zinc-300'}`}>
                                <div className="font-semibold">{dayLabels[di]}</div>
                                {ds ? <div className="font-mono">{ds.start}-{ds.end}</div> : <div>-</div>}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ─── CARGO RULES ─── */}
        <div className="border border-zinc-200 bg-white p-6" data-testid="cargo-rules-section">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50">
                <Briefcase size={20} className="text-blue-600" />
              </div>
              <div>
                <h2 className="font-heading font-semibold text-zinc-900">Reglas de Cargo</h2>
                <p className="text-xs text-zinc-500">Define turnos permitidos y dotacion minima por cargo</p>
              </div>
            </div>
            <button onClick={() => openRuleModal()}
              disabled={shiftTemplates.length === 0}
              className="btn-primary flex items-center gap-2 text-sm disabled:opacity-50"
              data-testid="add-rule-btn">
              <Plus size={16} /> Nueva Regla
            </button>
          </div>

          {shiftTemplates.length === 0 && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs p-3 mb-4">
              Primero debe crear plantillas de turno antes de definir reglas de cargo.
            </div>
          )}

          {cargoRules.length === 0 ? (
            <div className="text-center py-8 border border-dashed border-zinc-200">
              <Briefcase size={32} className="text-zinc-300 mx-auto mb-2" />
              <p className="text-sm text-zinc-400">No hay reglas de cargo configuradas</p>
              <p className="text-xs text-zinc-400 mt-1">El generador distribuira todos los trabajadores sin restricciones de cargo</p>
            </div>
          ) : (
            <div className="grid gap-2">
              {cargoRules.map(r => (
                <div key={r.id} className="flex items-center justify-between px-4 py-3 border border-zinc-100 hover:border-zinc-300 transition-colors" data-testid={`rule-item-${r.id}`}>
                  <div className="flex items-center gap-4">
                    <span className="inline-flex items-center justify-center w-9 h-9 bg-blue-100 text-blue-700 font-bold text-xs">
                      {r.min_workers}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-zinc-900">{r.cargo}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-xs text-zinc-500">Turnos:</span>
                        {(r.shift_codes || []).map(code => (
                          <span key={code} className="text-xs font-mono font-medium bg-emerald-100 text-emerald-700 px-1.5 py-0.5">{code}</span>
                        ))}
                        {r.is_variable && (
                          <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 ml-1">Variable</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => openRuleModal(r)} className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors" data-testid={`edit-rule-${r.id}`}>
                      <PencilSimple size={16} />
                    </button>
                    <button onClick={() => handleDeleteRule(r.id)} className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors" data-testid={`delete-rule-${r.id}`}>
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {cargosWithoutRules.length > 0 && cargoRules.length > 0 && (
            <div className="mt-4 bg-zinc-50 border border-zinc-200 text-xs text-zinc-500 p-3">
              <span className="font-medium text-zinc-700">Cargos sin regla:</span>{' '}
              {cargosWithoutRules.map(c => c.name).join(', ')}
              <span className="block mt-1 text-zinc-400">Los trabajadores de estos cargos se asignaran usando distribucion general.</span>
            </div>
          )}
        </div>

        {/* Cargos Management */}
        <div className="border border-zinc-200 bg-white p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-zinc-100"><Briefcase size={20} className="text-zinc-600" /></div>
            <div>
              <h2 className="font-heading font-semibold text-zinc-900">Cargos de Trabajadores</h2>
              <p className="text-sm text-zinc-500">Administre los cargos disponibles para asignar a trabajadores</p>
            </div>
          </div>
          <div className="flex gap-2 mb-4">
            <input type="text" value={newCargo} onChange={(e) => setNewCargo(e.target.value)}
              placeholder="Nombre del nuevo cargo..." className="form-input flex-1"
              onKeyDown={(e) => e.key === 'Enter' && handleAddCargo()} data-testid="new-cargo-input" />
            <button onClick={handleAddCargo} disabled={addingCargo || !newCargo.trim()}
              className="btn-primary flex items-center gap-2" data-testid="add-cargo-btn">
              <Plus size={18} /> Agregar
            </button>
          </div>
          <div className="space-y-1">
            {cargos.map((cargo) => (
              <div key={cargo.id}
                className={`flex items-center justify-between px-3 py-2 border border-zinc-100 ${!cargo.is_active ? 'opacity-50 bg-zinc-50' : 'bg-white'}`}
                data-testid={`cargo-item-${cargo.id}`}>
                <span className={`text-sm ${cargo.is_active ? 'text-zinc-900' : 'text-zinc-400 line-through'}`}>{cargo.name}</span>
                <button onClick={() => handleToggleCargo(cargo)}
                  className={`text-xs px-2 py-1 ${cargo.is_active ? 'text-red-600 hover:bg-red-50' : 'text-emerald-600 hover:bg-emerald-50'} transition-colors`}
                  data-testid={`toggle-cargo-${cargo.id}`}>
                  {cargo.is_active ? 'Desactivar' : 'Activar'}
                </button>
              </div>
            ))}
            {cargos.length === 0 && <p className="text-sm text-zinc-400 text-center py-4">No hay cargos registrados</p>}
          </div>
        </div>

        {/* Info Box */}
        <div className="border border-zinc-200 bg-zinc-50 p-6">
          <h3 className="font-heading font-semibold text-zinc-900 mb-2">Informacion del Sistema</h3>
          <ul className="text-sm text-zinc-600 space-y-1">
            <li>Los cambios se aplican inmediatamente despues de guardar.</li>
            <li>El limite mensual aplica al total acumulado del mes calendario.</li>
            <li>Si hay plantillas de turno y reglas de cargo, el generador automatico las usara.</li>
            <li>Sin plantillas, el generador usa el modo generico (manana/tarde).</li>
          </ul>
        </div>
      </div>

      {/* ─── Template Modal ─── */}
      <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
        <DialogContent className="sm:max-w-lg rounded-none border-zinc-200">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingTemplate ? 'Editar Plantilla de Turno' : 'Nueva Plantilla de Turno'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto pr-1">
            <div>
              <label className="form-label">Sucursal</label>
              <select
                value={templateForm.branch_id || ''}
                onChange={(e) => setTemplateForm({ ...templateForm, branch_id: e.target.value || null })}
                className="form-input"
                data-testid="template-branch-select"
              >
                <option value="">Global (todas las sucursales)</option>
                {branches.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="form-label">Codigo (letra)</label>
                <input type="text" value={templateForm.code}
                  onChange={(e) => setTemplateForm({ ...templateForm, code: e.target.value.toUpperCase().slice(0, 2) })}
                  className="form-input w-full text-center font-mono font-bold text-lg" placeholder="A"
                  maxLength={2} data-testid="template-code-input" />
              </div>
              <div>
                <label className="form-label">Nombre del turno</label>
                <input type="text" value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  className="form-input" placeholder="Ej: Turno Manana" data-testid="template-name-input" />
              </div>
            </div>

            {/* Areas / Cargos */}
            <div>
              <label className="form-label">Areas / Cargos que cubre</label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {cargos.filter(c => c.is_active).map(c => {
                  const sel = (templateForm.areas || []).includes(c.name);
                  return (
                    <button key={c.id} type="button"
                      onClick={() => {
                        const cur = templateForm.areas || [];
                        setTemplateForm({
                          ...templateForm,
                          areas: sel ? cur.filter(a => a !== c.name) : [...cur, c.name],
                        });
                      }}
                      className={`px-2 py-1 text-xs border transition-colors ${
                        sel ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400'
                      }`}
                      data-testid={`tpl-area-${c.name}`}>
                      {c.name}
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-zinc-400 mt-1">Seleccione las areas que participan en este turno (opcional)</p>
            </div>

            {/* Horario base */}
            <div>
              <label className="form-label">Horario base (predeterminado)</label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-zinc-500">Hora inicio</label>
                  <input type="time" value={templateForm.start_time}
                    onChange={(e) => setTemplateForm({ ...templateForm, start_time: e.target.value })}
                    className="form-input font-mono" data-testid="template-start-input" />
                </div>
                <div>
                  <label className="text-[10px] text-zinc-500">Hora fin</label>
                  <input type="time" value={templateForm.end_time}
                    onChange={(e) => setTemplateForm({ ...templateForm, end_time: e.target.value })}
                    className="form-input font-mono" data-testid="template-end-input" />
                </div>
              </div>
            </div>

            {/* Horario por dia */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="form-label mb-0">Horario por dia (Lun-Dom)</label>
                <button type="button"
                  onClick={() => {
                    if (Object.keys(templateForm.schedule || {}).length > 0) {
                      setTemplateForm({ ...templateForm, schedule: {} });
                    } else {
                      const base = {};
                      ['lun','mar','mie','jue','vie','sab'].forEach(d => {
                        base[d] = { start: templateForm.start_time || '09:00', end: templateForm.end_time || '17:00' };
                      });
                      setTemplateForm({ ...templateForm, schedule: base });
                    }
                  }}
                  className="text-[10px] text-blue-600 hover:text-blue-800"
                  data-testid="toggle-schedule-btn">
                  {Object.keys(templateForm.schedule || {}).length > 0 ? 'Usar horario unico' : 'Personalizar por dia'}
                </button>
              </div>
              {Object.keys(templateForm.schedule || {}).length > 0 ? (
                <div className="space-y-1.5">
                  {[
                    { key: 'lun', label: 'Lunes' },
                    { key: 'mar', label: 'Martes' },
                    { key: 'mie', label: 'Miercoles' },
                    { key: 'jue', label: 'Jueves' },
                    { key: 'vie', label: 'Viernes' },
                    { key: 'sab', label: 'Sabado' },
                    { key: 'dom', label: 'Domingo' },
                  ].map(({ key, label }) => {
                    const ds = (templateForm.schedule || {})[key];
                    const active = !!ds;
                    return (
                      <div key={key} className={`flex items-center gap-2 px-2 py-1.5 border ${active ? 'border-emerald-200 bg-emerald-50/50' : 'border-zinc-100 bg-zinc-50'}`}
                        data-testid={`sched-day-${key}`}>
                        <button type="button"
                          onClick={() => {
                            const sched = { ...(templateForm.schedule || {}) };
                            if (active) {
                              delete sched[key];
                            } else {
                              sched[key] = { start: templateForm.start_time || '09:00', end: templateForm.end_time || '17:00' };
                            }
                            setTemplateForm({ ...templateForm, schedule: sched });
                          }}
                          className={`w-5 h-5 flex items-center justify-center border text-[10px] font-bold ${active ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-zinc-400 border-zinc-300'}`}>
                          {active ? '\u2713' : ''}
                        </button>
                        <span className="text-xs font-medium text-zinc-700 w-20">{label}</span>
                        {active ? (
                          <>
                            <input type="time" value={ds.start}
                              onChange={(e) => {
                                const sched = { ...(templateForm.schedule || {}) };
                                sched[key] = { ...sched[key], start: e.target.value };
                                setTemplateForm({ ...templateForm, schedule: sched });
                              }}
                              className="form-input py-1 px-1.5 text-xs font-mono w-24" />
                            <span className="text-zinc-400 text-xs">-</span>
                            <input type="time" value={ds.end}
                              onChange={(e) => {
                                const sched = { ...(templateForm.schedule || {}) };
                                sched[key] = { ...sched[key], end: e.target.value };
                                setTemplateForm({ ...templateForm, schedule: sched });
                              }}
                              className="form-input py-1 px-1.5 text-xs font-mono w-24" />
                          </>
                        ) : (
                          <span className="text-xs text-zinc-400 italic">Libre</span>
                        )}
                      </div>
                    );
                  })}
                  {(() => {
                    const sched = templateForm.schedule || {};
                    let totalMin = 0;
                    Object.values(sched).forEach(ds => {
                      if (ds?.start && ds?.end) {
                        const [sh, sm] = ds.start.split(':').map(Number);
                        const [eh, em] = ds.end.split(':').map(Number);
                        let mins = (eh * 60 + em) - (sh * 60 + sm);
                        if (mins < 0) mins += 24 * 60;
                        totalMin += mins;
                      }
                    });
                    const totalH = (totalMin / 60).toFixed(1);
                    const colacion = Object.keys(sched).length * 0.5;
                    const efectivas = (totalMin / 60 - colacion).toFixed(1);
                    return (
                      <div className="flex items-center justify-between mt-2 p-2 bg-zinc-100 text-xs">
                        <span className="text-zinc-600">Total semanal: <strong className="text-zinc-900">{totalH}h</strong> ({efectivas}h efectivas est.)</span>
                        <span className={`font-medium ${parseFloat(efectivas) > 44 ? 'text-red-600' : parseFloat(efectivas) >= 40 ? 'text-amber-600' : 'text-emerald-600'}`}>
                          {parseFloat(efectivas) > 44 ? 'Excede 44h' : parseFloat(efectivas) >= 40 ? 'Cerca de 44h' : 'OK'}
                        </span>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <p className="text-xs text-zinc-400 p-2 border border-dashed border-zinc-200 text-center">
                  Se usara el horario base para todos los dias. Haga clic en "Personalizar por dia" para definir horarios diferentes por dia.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateModal(false)} className="rounded-none">Cancelar</Button>
            <Button onClick={handleSaveTemplate} className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-template-btn">
              {editingTemplate ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Cargo Rule Modal ─── */}
      <Dialog open={showRuleModal} onOpenChange={setShowRuleModal}>
        <DialogContent className="sm:max-w-md rounded-none border-zinc-200">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingRule ? 'Editar Regla de Cargo' : 'Nueva Regla de Cargo'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="form-label">Sucursal</label>
              <select
                value={ruleForm.branch_id || ''}
                onChange={(e) => setRuleForm({ ...ruleForm, branch_id: e.target.value || null })}
                className="form-input"
                data-testid="rule-branch-select"
              >
                <option value="">Global (todas las sucursales)</option>
                {branches.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Cargo</label>
              {editingRule ? (
                <p className="text-sm font-medium text-zinc-900 py-1">{ruleForm.cargo}</p>
              ) : (
                <select value={ruleForm.cargo}
                  onChange={(e) => setRuleForm({ ...ruleForm, cargo: e.target.value })}
                  className="form-input" data-testid="rule-cargo-select">
                  <option value="">Seleccionar cargo...</option>
                  {cargosWithoutRules.map(c => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <label className="form-label">Turnos permitidos</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {shiftTemplates.map(t => {
                  const selected = ruleForm.shift_codes.includes(t.code);
                  return (
                    <button key={t.code} type="button" onClick={() => toggleShiftCode(t.code)}
                      className={`px-3 py-2 text-sm font-mono font-medium border transition-colors ${
                        selected ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400'
                      }`} data-testid={`rule-shift-toggle-${t.code}`}>
                      {t.code} <span className="text-xs font-sans opacity-75">({t.start_time})</span>
                    </button>
                  );
                })}
              </div>
              {shiftTemplates.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">No hay plantillas disponibles. Cree plantillas primero.</p>
              )}
            </div>
            <div>
              <label className="form-label">Dotacion minima</label>
              <input type="number" value={ruleForm.min_workers}
                onChange={(e) => setRuleForm({ ...ruleForm, min_workers: Math.max(1, parseInt(e.target.value) || 1) })}
                className="form-input w-24 text-center font-mono" min="1" max="50" data-testid="rule-min-workers-input" />
              <p className="text-xs text-zinc-400 mt-1">Minimo de trabajadores de este cargo que deben estar presentes por dia</p>
            </div>
            <div className="flex items-center justify-between py-2 border-t border-zinc-100">
              <div>
                <p className="text-sm font-medium text-zinc-900">Dotacion variable</p>
                <p className="text-xs text-zinc-500">Si esta activo, la dotacion escala con la demanda (+30% en alta, -20% en baja)</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={ruleForm.is_variable}
                  onChange={(e) => setRuleForm({ ...ruleForm, is_variable: e.target.checked })}
                  className="sr-only peer" data-testid="rule-variable-toggle" />
                <div className="w-11 h-6 bg-zinc-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-zinc-900 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-zinc-300 after:border after:h-5 after:w-5 after:transition-all peer-checked:bg-zinc-900"></div>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRuleModal(false)} className="rounded-none">Cancelar</Button>
            <Button onClick={handleSaveRule} className="rounded-none bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-rule-btn">
              {editingRule ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
